import { MouseEvent, useCallback, useLayoutEffect } from 'react';
import { useToggle } from 'react-use';
import { LoadingTracker } from 'src/components/common/LoadingTracker';
import { MarketplacePaymentsTab } from 'src/components/MarketplacePayments';
import { MarketplacePaymentDeeplinkListener } from 'src/components/MarketplacePayments/MarketplacePaymentDeeplinkListener';
import { MarketplacePaymentsToolbar } from 'src/components/MarketplacePayments/PaymentsToolbar/MarketplacePaymentsToolbar';
import { Content, useContent } from 'src/contexts/ContentContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { vars } from 'src/core/themes';
import * as Tabs from 'src/core/ui/Tabs';
import { LaunchBulkUploadMarketplacePaymentLineDialog } from 'src/dialogs/BulkEdits/BulkUploadMarketplacePaymentLineDialog';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutContent } from 'src/navigations/LayoutContent';
import {
  DefaultMarketplacePaymentQuery,
  EmptyMarketplacePaymentQuery,
  PaymentTab,
} from 'src/navigations/Routes/Payments/Payments.constants';
import {
  marketplacePaymentQueryValueTransformToUrl,
  marketplacePaymentValueTransformFromUrl,
} from 'src/navigations/Routes/Payments/Payments.utils';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import {
  Feature,
  MarketplacePaymentQuery,
  UserSetting,
} from 'src/WebApiController';

import { MainRoute } from '../MainRoute';
import * as styles from './Payments.css';
import { PaymentsSearchBox } from './PaymentsSearchBox';

export function Payments() {
  const paymentsTitle = useContent(ContentId.Payments);
  const { registerComponent } = usePageLoadStatus();
  const isMobile = useMatchMedia('mobile');
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);
  const hasMarketplacePaymentsSearchFeature = useUserHasFeature(
    Feature.MarketplacePaymentSearch
  );
  const hasBulkUploadMarketplacePaymentLineFeature = useUserHasFeature(
    Feature.BulkUploadMarketplacePaymentLine
  );

  useLayoutEffect(() => {
    registerComponent('MarketplacePaymentsTab');
  }, [registerComponent]);
  const onColumnSettingButtonClickHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isColumnModalOpen) {
        toggleColumnModal(true);
      }
    },
    [isColumnModalOpen, toggleColumnModal]
  );

  return (
    <FilterQueryContextProvider<MarketplacePaymentQuery>
      initialQuery={DefaultMarketplacePaymentQuery}
      viewModeSettingId={UserSetting.PurchasePageViewMode}
      emptyQuery={EmptyMarketplacePaymentQuery}
      queryValueTransformToUrl={marketplacePaymentQueryValueTransformToUrl}
      queryValueTransformFromUrl={marketplacePaymentValueTransformFromUrl}
      saveQueryInUrl
    >
      <LayoutContent
        mainRoute={MainRoute.Payments}
        centerContent={
          hasMarketplacePaymentsSearchFeature && <PaymentsSearchBox />
        }
        rightContent={
          hasBulkUploadMarketplacePaymentLineFeature && (
            <LaunchBulkUploadMarketplacePaymentLineDialog variant="regular" />
          )
        }
        routeTitle={paymentsTitle}
      >
        {isMobile && hasMarketplacePaymentsSearchFeature && (
          <div style={{ padding: `0 ${vars.spacing.lg}` }}>
            <PaymentsSearchBox />
          </div>
        )}

        <MarketplacePaymentDeeplinkListener>
          <Tabs.Root
            defaultValue={PaymentTab.MarketplacePayments}
            className={styles.paymentsPageTabsRoot}
          >
            <Tabs.List className={styles.tabsWrapper}>
              <Tabs.Trigger value={PaymentTab.MarketplacePayments}>
                <Content id={ContentId.MarketplacePayments} />
              </Tabs.Trigger>
            </Tabs.List>

            <MarketplacePaymentsToolbar
              onColumnSettingButtonClickHandler={
                onColumnSettingButtonClickHandler
              }
            />

            <Tabs.Content
              value={PaymentTab.MarketplacePayments}
              className={styles.tabWrapper}
            >
              <LoadingTracker componentName="MarketplacePaymentsTab">
                <MarketplacePaymentsTab />
              </LoadingTracker>
            </Tabs.Content>
          </Tabs.Root>
          {isColumnModalOpen && (
            <ColumnSettingsModal
              onClose={toggleColumnModal}
              sectionType={SectionType.MarketplacePaymentsTable}
            />
          )}
        </MarketplacePaymentDeeplinkListener>
      </LayoutContent>
    </FilterQueryContextProvider>
  );
}
